import { useState } from 'react';
import styled from 'styled-components';
import { ImplementationKind } from '@finch-api/common/dist/internal/connect/authorize';
import {
  StyledInput,
  StyledCheckbox,
} from 'components/Input/CheckboxInput';
import LoaderButton from 'components/Button/LoaderButton';
import COLORS from 'constants/colors';
import { AppLink } from 'components/Link/AppLink';
import { AUTOMATED_AUTH_INSTRUCTIONS } from 'constants/automated-auth-instructions';
import { PayrollProviderId } from 'constants/types';

const SYNC_AND_CLONE_HELP_CENTER_URL =
  AUTOMATED_AUTH_INSTRUCTIONS[PayrollProviderId.PAYCOM]?.[
    ImplementationKind.CREDENTIAL
  ];

const Title = styled.h2`
  color: var(--Updated-Finch-Black, #161616);
  font-family: Inter;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  text-align: center;
  margin: 0;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: var(--Updated-Finch-Black, #161616);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  text-align: center;
  margin: 0;
  margin-bottom: 16px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-grow: 1;
`;

const ChecklistCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
`;

const CardCheckbox = styled(StyledCheckbox)`
  align-items: flex-start;
  margin-top: 2px;
`;

const CheckboxContent = styled.label`
  display: flex;
  flex-direction: column;
  flex: 1;
  cursor: pointer;
`;

const CheckboxLabel = styled.span`
  color: var(--Updated-Finch-Black, #161616);
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`;

const CheckboxDescription = styled.span`
  color: var(--Updated-Finch-Black, #161616);
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  margin-top: 4px;
`;

const GuidePrompt = styled(CheckboxDescription)`
  font-weight: 600;

  a {
    color: ${COLORS.FINCH.PURPLE};
    font-weight: 600;
    text-decoration: underline;
  }
`;

interface PaycomSyncCloneCheckProps {
  onContinue: () => void;
}

const PaycomSyncCloneCheck = ({
  onContinue,
}: PaycomSyncCloneCheckProps) => {
  const [clientCodeConfirmed, setClientCodeConfirmed] = useState(false);
  const [syncCloneConfirmed, setSyncCloneConfirmed] = useState(false);

  const canContinue = clientCodeConfirmed && syncCloneConfirmed;

  return (
    <>
      <Title>Before you connect</Title>
      <Subtitle>
        Make sure your Paycom account is ready. This only takes a minute.
      </Subtitle>
      <CheckboxContainer>
        <ChecklistCard>
          <CardCheckbox>
            <StyledInput
              type="checkbox"
              id="client-code-confirmed"
              onChange={(e) => setClientCodeConfirmed(e.target.checked)}
              checked={clientCodeConfirmed}
              disabled={false}
              color={COLORS.FINCH.BLACK}
            />
          </CardCheckbox>
          <CheckboxContent htmlFor="client-code-confirmed">
            <CheckboxLabel>I know my 5-digit client code</CheckboxLabel>
            <CheckboxDescription>
              Found on the Paycom homepage to the left of ALLDEPTS in the white
              menu bar
            </CheckboxDescription>
          </CheckboxContent>
        </ChecklistCard>
        <ChecklistCard>
          <CardCheckbox>
            <StyledInput
              type="checkbox"
              id="sync-clone-confirmed"
              onChange={(e) => setSyncCloneConfirmed(e.target.checked)}
              checked={syncCloneConfirmed}
              disabled={false}
              color={COLORS.FINCH.BLACK}
            />
          </CardCheckbox>
          <CheckboxContent htmlFor="sync-clone-confirmed">
            <CheckboxLabel>
              If I manage multiple client codes: Sync &amp; Clone is enabled
            </CheckboxLabel>
            <CheckboxDescription>
              If you manage more than one client code, Sync &amp; Clone must be
              enabled to keep your connection active long-term. Without it,
              you&apos;ll have to reconnect in 30 days.
            </CheckboxDescription>
            <GuidePrompt>
              Open{' '}
              <AppLink
                href={SYNC_AND_CLONE_HELP_CENTER_URL}
                rel="noreferrer"
                target="_blank"
                showInFrame
              >
                this guide
              </AppLink>{' '}
              to check if Sync &amp; Clone is required
            </GuidePrompt>
          </CheckboxContent>
        </ChecklistCard>
      </CheckboxContainer>
      <LoaderButton
        disabled={!canContinue}
        type="button"
        text="Continue"
        onClick={onContinue}
      />
    </>
  );
};

export default PaycomSyncCloneCheck;
